import React, { useCallback, useState } from 'react';
import Cropper from 'react-easy-crop';
import imageCompression from 'browser-image-compression';
import { X, Check } from 'lucide-react';

/**
 * AvatarCropModal (iter95)
 *
 * Full-screen modal that:
 *   1. Loads a user-selected image file
 *   2. Lets the user crop it as a square (circular preview overlay)
 *   3. Zooms with a slider
 *   4. On confirm: crops to a square, compresses to ≤500KB / 512x512, and
 *      invokes `onConfirm(compressedFile)`.
 *
 * Purely client-side — the resulting File is what the parent uploads via
 * whatever endpoint / method it already uses.
 */

const OUTPUT_MAX_SIZE_MB = 0.5;   // 500 KB target
const OUTPUT_MAX_DIMENSION = 512; // 512x512 max — plenty for an avatar

// Reads an image file into an <img> element so we can draw it to canvas.
const createImage = (url) => new Promise((resolve, reject) => {
  const img = new Image();
  img.addEventListener('load', () => resolve(img));
  img.addEventListener('error', (err) => reject(err));
  img.src = url;
});

// Crops the source image using pixel coordinates from react-easy-crop and
// returns a Blob (JPEG).
const cropToBlob = async (imageSrc, pixelCrop) => {
  const image = await createImage(imageSrc);
  const canvas = document.createElement('canvas');
  const ctx = canvas.getContext('2d');
  canvas.width = pixelCrop.width;
  canvas.height = pixelCrop.height;
  ctx.drawImage(
    image,
    pixelCrop.x, pixelCrop.y, pixelCrop.width, pixelCrop.height,
    0, 0, pixelCrop.width, pixelCrop.height,
  );
  return new Promise((resolve) => canvas.toBlob(resolve, 'image/jpeg', 0.92));
};

const AvatarCropModal = ({ file, onCancel, onConfirm }) => {
  const [imageSrc, setImageSrc] = useState(null);
  const [crop, setCrop] = useState({ x: 0, y: 0 });
  const [zoom, setZoom] = useState(1);
  const [croppedAreaPixels, setCroppedAreaPixels] = useState(null);
  const [processing, setProcessing] = useState(false);

  // Load the file once when it's passed in.
  React.useEffect(() => {
    if (!file) return;
    const reader = new FileReader();
    reader.addEventListener('load', () => setImageSrc(reader.result));
    reader.readAsDataURL(file);
  }, [file]);

  const onCropComplete = useCallback((_area, areaPixels) => {
    setCroppedAreaPixels(areaPixels);
  }, []);

  const handleConfirm = async () => {
    if (!imageSrc || !croppedAreaPixels) return;
    setProcessing(true);
    try {
      const rawBlob = await cropToBlob(imageSrc, croppedAreaPixels);
      // Wrap in a File so the caller can FormData.append it directly.
      const rawFile = new File([rawBlob], 'avatar.jpg', { type: 'image/jpeg' });
      // Compress: caps at 500KB, 512x512, and re-encodes as WebP if the
      // browser supports it (falls back to JPEG). This shrinks a typical
      // 2-3MB phone photo down to ~80-150KB with negligible visual loss.
      const compressed = await imageCompression(rawFile, {
        maxSizeMB: OUTPUT_MAX_SIZE_MB,
        maxWidthOrHeight: OUTPUT_MAX_DIMENSION,
        useWebWorker: true,
        fileType: 'image/jpeg',
        initialQuality: 0.88,
      });
      onConfirm(compressed);
    } catch (e) {
      console.error('AvatarCropModal.handleConfirm failed:', e);
      onConfirm(null);
    } finally {
      setProcessing(false);
    }
  };

  if (!file) return null;

  return (
    <div
      className="fixed inset-0 z-[100] bg-black/85 flex flex-col"
      data-testid="avatar-crop-modal"
    >
      <div className="flex items-center justify-between px-4 py-3 text-white">
        <button
          type="button"
          onClick={onCancel}
          disabled={processing}
          data-testid="avatar-crop-cancel"
          className="p-2 rounded-full hover:bg-white/10 transition-colors disabled:opacity-50"
          aria-label="Cancel"
        >
          <X size={22} />
        </button>
        <h2 className="text-sm font-semibold">Crop Profile Picture</h2>
        <button
          type="button"
          onClick={handleConfirm}
          disabled={processing || !croppedAreaPixels}
          data-testid="avatar-crop-confirm"
          className="px-4 py-1.5 text-sm font-bold rounded-full bg-[#ff8500] text-white hover:bg-[#e57500] transition-colors disabled:opacity-50 flex items-center gap-1"
        >
          <Check size={16} /> {processing ? 'Saving…' : 'Save'}
        </button>
      </div>

      <div className="relative flex-1 bg-black">
        {imageSrc && (
          <Cropper
            image={imageSrc}
            crop={crop}
            zoom={zoom}
            aspect={1}
            cropShape="round"
            showGrid={false}
            onCropChange={setCrop}
            onZoomChange={setZoom}
            onCropComplete={onCropComplete}
          />
        )}
      </div>

      <div className="bg-black/90 px-6 py-4 flex items-center gap-3">
        <span className="text-white/70 text-xs">Zoom</span>
        <input
          type="range"
          min={1}
          max={3}
          step={0.05}
          value={zoom}
          onChange={(e) => setZoom(Number(e.target.value))}
          className="flex-1 accent-[#ff8500]"
          data-testid="avatar-crop-zoom"
        />
      </div>
    </div>
  );
};

export default AvatarCropModal;
